<template>
  <div class="contents">
    <BundledServicesActiveModal @confirm="onConfirmBundledRefund" />

    <h2 class="heading-1 mb-24" data-qa="title-option">
      {{ i18n(translations.heading) }}
    </h2>

    <BundleInfo
      v-if="displayBundleInfo"
      :cancellable-bundle="hasStraightRefundOptionAvailable"
      class="mb-24"
    />

    <WarrantyExclusionInfo
      v-if="hasWarrantyExclusion"
      class="mb-24"
      data-qa="warranty-exclusion-info-block"
      data-test="warranty-exclusion-info-block"
    />

    <LrrInfoBlock v-if="lrrIsOngoing" class="mb-24" />

    <ul v-if="formatedResolutionOptions" class="mb-24 list-none space-y-12">
      <li
        v-for="(resolutionOption, index) in formatedResolutionOptions"
        :key="index"
      >
        <component
          :is="getResolutionOptionCard(resolutionOption.type)"
          v-bind="getLoadingButtonIfChatOptions(resolutionOption.type)"
          @select="onContinue"
        />
      </li>
    </ul>

    <div
      v-if="repairOptionsUnavailable"
      class="mb-24 flex w-full items-center gap-x-8"
    >
      <RevTooltip position="top">
        <template #trigger="{ show, hide }">
          <IconInfo
            @focusin="show"
            @focusout="hide"
            @mouseleave="hide"
            @mouseover="show"
          />
        </template>

        {{ i18n(translations.repairUnavailableTooltipContent) }}
      </RevTooltip>

      <p class="body-2">
        {{ i18n(translations.repairUnavailableInfoText) }}
      </p>
    </div>

    <div
      v-if="
        hasRemoteAssistanceOptionAvailable &&
        !hasLiveChatOptionAvailable &&
        isInWarranty
      "
      class="mb-72"
    >
      <p class="body-2">
        {{ i18n(translations.getRemotAssistance) }}

        <RevLink
          data-qa="resolution-option-remote-assistance-link"
          @click="getHelpByEmail"
        >
          {{ i18n(translations.helpByEmail) }}
        </RevLink>
      </p>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, watch } from 'vue'

import {
  DECLARE_WARRANTY_STATE_ENUM,
  type GetResolutionOptionsTypeEnum,
  RESOLUTION_OPTION_TYPE_ENUM,
} from '@backmarket/http-api/src/api-specs-resolution-engine/types/common'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevLink } from '@ds/components/Link'
import { openModal } from '@ds/components/ModalBase'
import { RevTooltip } from '@ds/components/Tooltip'
import { IconInfo } from '@ds/icons/IconInfo'

import type {
  IntercomEvents,
  MachineSend,
  ResolutionOptionsEvents,
} from '@/scopes/customer-care/resolution/machine/ResolutionFlow.machine.types'
import BundledServicesActiveModal from '@/scopes/customer-care/resolution/modals/BundledServicesActiveModal/BundledServicesActiveModal.vue'
import { RESOLUTION_FLOW_MODALS_NAMES } from '@/scopes/customer-care/resolution/modals/names'
import type { ResolutionOption } from '~/api/resolution-engine/types/GetResolutionOptions.types'
import { useCheckForLrr } from '~/scopes/care-commons/composables/useCheckForLrr'
import LrrInfoBlock from '~/scopes/customer-care/resolution/components/common/LrrInfoBlock/LrrInfoBlock.vue'

import { resolutionOptionsConfig } from './ResolutionOptionScreen.config'
import translations from './ResolutionOptionScreen.translations'
import type { ResolutionOptionScreenProps } from './ResolutionOptionScreen.types'
import BundleInfo from './components/BundleInfo/BundleInfo.vue'
import WarrantyExclusionInfo from './components/WarrantyExclusionInfo/WarrantyExclusionInfo.vue'
import { isResolutionOptionAvailable } from './utils/resolutionOptionAvailable'

const props = defineProps<ResolutionOptionScreenProps>()

const i18n = useI18n()
const { lrrIsOngoing } = useCheckForLrr()
const { openErrorToast } = useTheToast()

const send = props.machineSend as MachineSend<
  ResolutionOptionsEvents | IntercomEvents
>

const hasBundlePlan = computed(() => !isEmpty(props.bundledMobilePlan))

const hasLiveChatOptionAvailable = computed(() =>
  isResolutionOptionAvailable(
    props.resolutionOptions,
    RESOLUTION_OPTION_TYPE_ENUM.liveChat,
  ),
)

const hasRefundOptionAvailable = computed(() =>
  isResolutionOptionAvailable(
    props.resolutionOptions,
    RESOLUTION_OPTION_TYPE_ENUM.refund,
  ),
)

const hasRemoteAssistanceOptionAvailable = computed(() =>
  isResolutionOptionAvailable(
    props.resolutionOptions,
    RESOLUTION_OPTION_TYPE_ENUM.remoteAssistance,
  ),
)

const hasStraightRefundOptionAvailable = computed(() =>
  isResolutionOptionAvailable(
    props.resolutionOptions,
    RESOLUTION_OPTION_TYPE_ENUM.straightRefund,
  ),
)

const hasSubmitError = computed(() =>
  props.machineState.matches('RESOLUTION_OPTION.INTERCOM.LOADING_FAILED'),
)

const hasWarrantyExclusion = computed(
  () => props.declaredWarrantyState !== DECLARE_WARRANTY_STATE_ENUM.inWarranty,
)

const isInWarranty = computed(
  () => props.declaredWarrantyState === DECLARE_WARRANTY_STATE_ENUM.inWarranty,
)

const displayBundleInfo = computed(
  () =>
    hasBundlePlan.value &&
    (hasRefundOptionAvailable.value || hasStraightRefundOptionAvailable.value),
)

const formatedResolutionOptions = computed(() => {
  if (!hasLiveChatOptionAvailable.value && isInWarranty.value) {
    return props.resolutionOptions.filter(
      (item) => item.type !== RESOLUTION_OPTION_TYPE_ENUM.remoteAssistance,
    )
  }

  return props.resolutionOptions
})

function getResolutionOptionCard(type: GetResolutionOptionsTypeEnum) {
  if (type in resolutionOptionsConfig) {
    return resolutionOptionsConfig[type]
  }

  return undefined
}

function onConfirmBundledRefund() {
  const selectedOption = props.resolutionOptions.find(
    (option) => option.type === RESOLUTION_OPTION_TYPE_ENUM.refund,
  ) as ResolutionOption

  send({
    type: 'CONTINUE',
    payload: {
      resolutionOption: selectedOption,
    },
  })
}

function onContinue(event: GetResolutionOptionsTypeEnum) {
  const selectedOption = props.resolutionOptions.find(
    (option) => option.type === event,
  ) as ResolutionOption

  const requireConfirmation =
    hasBundlePlan.value &&
    selectedOption.type === RESOLUTION_OPTION_TYPE_ENUM.refund

  if (requireConfirmation) {
    openModal(RESOLUTION_FLOW_MODALS_NAMES.bundledServicesActiveModal)

    return
  }

  send({
    type: 'CONTINUE',
    payload: {
      resolutionOption: selectedOption,
    },
  })
}

const displayLoadingButton = computed(() =>
  props.machineState.hasTag('loading_button'),
)

function getLoadingButtonIfChatOptions(type: GetResolutionOptionsTypeEnum) {
  if (
    type === RESOLUTION_OPTION_TYPE_ENUM.intercomAsyncChat ||
    type === RESOLUTION_OPTION_TYPE_ENUM.intercomLiveChat
  ) {
    return { loading: displayLoadingButton.value }
  }

  return {}
}

function getHelpByEmail() {
  onContinue(RESOLUTION_OPTION_TYPE_ENUM.remoteAssistance)
}

watch(
  hasSubmitError,
  (isError) => {
    if (isError) {
      openErrorToast({
        title: i18n(translations.errorToastTitle),
        content: i18n(translations.errorToastDescription),
        closeCallback: () => send('CLOSE'),
        name: 'resolution-flow-intercom-error-toast',
      })
    }
  },
  { immediate: true },
)
</script>
